import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import logger from '../utils/logger';
import { AuthContext } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    CheckCircleIcon, 
    XCircleIcon, 
    ClockIcon, 
    TrophyIcon, 
    ArrowLeftIcon,
    ShieldCheckIcon,
    ArrowDownTrayIcon,
    AcademicCapIcon,
    BookOpenIcon,
    LightBulbIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const QuizPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);
    const certificateRef = useRef(null);
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [timeLeft, setTimeLeft] = useState(null);
    const [showWarning, setShowWarning] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [certificateData, setCertificateData] = useState(null);

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const config = { headers: { 'x-auth-token': token } };
                const res = await axios.get(`${API_BASE_URL}/api/quizzes/${id}`, config);
                setQuiz(res.data);
                
                // Eğer sertifika zaten alınmışsa quiz'i gösterme
                if (res.data.hasCertificate) {
                    setError("You have already completed this quiz and earned your certificate!");
                    setResult({ 
                        alreadyCompleted: true, 
                        certificateId: res.data.certificateId 
                    });
                } else {
                    // 30 dakika timer (isteğe bağlı)
                    setTimeLeft(30 * 60);
                }
            } catch (err) { 
                setError("An error occurred while loading the quiz or you do not have permission.");
                toast.error('Quiz could not be loaded!');
            }
            finally { setLoading(false); }
        };
        if (token) fetchQuiz();
    }, [id, token]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAnswerChange = (qIndex, option) => setAnswers({ ...answers, [qIndex]: option });

    const handleSubmit = useCallback(async (e, autoSubmit = false) => {
        if (e) e.preventDefault();
        setError('');
        
        const unansweredCount = quiz.questions.length - Object.keys(answers).length;
        if (unansweredCount > 0 && !autoSubmit) {
            if (!window.confirm(`${unansweredCount} question(s) unanswered. Do you still want to submit?`)) {
                return;
            }
        }

        const loadingToast = toast.loading('Evaluating quiz...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            const body = { answers };
            const res = await axios.post(`${API_BASE_URL}/api/quizzes/${id}/submit`, body, config);
            setResult(res.data);
            toast.success('Quiz completed successfully!', { id: loadingToast });
        } catch (err) { 
            setError(err.response?.data?.msg || "An error occurred while submitting the quiz.");
            toast.error('Quiz could not be submitted!', { id: loadingToast });
        }
    }, [quiz, answers, token, id]);

    // Load certificate data when result is set
    useEffect(() => {
        const loadCertificateData = async () => {
            if (result && result.certificateId) {
                try {
                    logger.log('Loading certificate:', result.certificateId);
                    const res = await axios.get(`${API_BASE_URL}/api/certificates/verify/${result.certificateId}`);
                    logger.log('Certificate loaded:', res.data);
                    setCertificateData(res.data);
                } catch (error) {
                    logger.error('Error loading certificate:', error);
                    toast.error('Failed to load certificate data');
                }
            }
        };
        loadCertificateData();
    }, [result]);
    
    // Check if ref is attached after certificateData loads
    useEffect(() => {
        if (certificateData) {
            // React render cycle bitmesini bekle
            setTimeout(() => {
                if (certificateRef.current) {
                    logger.log('Certificate ref attached successfully!');
                } else {
                    logger.warn('Certificate ref not attached yet, retrying...');
                    // Bir kez daha dene
                    setTimeout(() => {
                        if (certificateRef.current) {
                            logger.log('Certificate ref attached on retry!');
                        } else {
                            logger.error('Certificate ref failed to attach - hidden div might not be rendering');
                        }
                    }, 500);
                }
            }, 200);
        }
    }, [certificateData]);

    // Zamanlayıcı
    useEffect(() => {
        if (timeLeft === null || timeLeft <= 0 || result) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleSubmit(null, true); // Zaman dolduğunda otomatik gönder
                    return 0;
                }
                if (prev === 300 && !showWarning) { // Warning at 5 minutes remaining
                    toast.error('Warning! Only 5 minutes remaining!');
                    setShowWarning(true);
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [timeLeft, result, showWarning, handleSubmit]);

    const progress = quiz ? ((Object.keys(answers).length / quiz.questions.length) * 100) : 0;

    if (loading) return <Spinner />;
    if (error && !quiz) return (
        <div className="card p-8 text-center max-w-lg mx-auto">
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <p className="text-red-500 text-lg">{error}</p>
            <button onClick={() => navigate(-1)} className="btn-secondary mt-4">
                <ArrowLeftIcon className="h-5 w-5 inline mr-2" />
                Go Back
            </button>
        </div>
    );
    if (!quiz) return <div className="card p-8 text-center">Quiz not found.</div>;

    const handleShareLinkedIn = () => {
        const certificateUrl = `${window.location.origin}/verify/${result.certificateId}`;
        // LinkedIn API değişti - artık text parametresi desteklenmiyor, sadece URL paylaşılıyor
        const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certificateUrl)}`;
        window.open(linkedInUrl, '_blank', 'width=600,height=600');
    };

    const handleShareTwitter = () => {
        const certificateUrl = `${window.location.origin}/verify/${result.certificateId}`;
        const text = `I just completed "${quiz.title}" at @AircoreDynamics! 🎓✨ Check out my certificate:`;
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(certificateUrl)}`;
        window.open(twitterUrl, '_blank', 'width=600,height=600');
    };

    const handleDownloadCertificate = async () => {
        if (!certificateData) {
            toast.error('Certificate is loading, please wait...');
            return;
        }
        
        setDownloading(true);
        const loadingToast = toast.loading('Preparing PDF...');
        
        try {
            // İlk önce hidden div'i render ettir
            const parentDiv = document.getElementById('certificate-pdf-container');
            if (!parentDiv) {
                toast.error('Certificate element not found');
                setDownloading(false);
                return;
            }
            
            parentDiv.classList.remove('hidden');
            parentDiv.style.position = 'absolute';
            parentDiv.style.left = '-9999px';
            parentDiv.style.top = '0';
            parentDiv.style.visibility = 'visible';
            
            // Ref'in attach olmasını bekle
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (!certificateRef.current) {
                toast.error('Certificate not ready, please try again');
                parentDiv.classList.add('hidden');
                setDownloading(false);
                return;
            }
            
            const element = certificateRef.current;
            
            // Wait for fonts and images to load
            await new Promise(resolve => setTimeout(resolve, 300));
            
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                width: 1122,
                height: 794,
                allowTaint: true,
                foreignObjectRendering: false
            });
            
            // Hide element again
            parentDiv.classList.add('hidden');
            parentDiv.style.position = '';
            parentDiv.style.left = '';
            parentDiv.style.top = '';
            parentDiv.style.visibility = '';
            
            const imgData = canvas.toDataURL('image/png', 1.0);
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true
            });
            
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, '', 'FAST');
            pdf.save(`Certificate_${certificateData.certificateId}.pdf`);
            
            toast.success('Certificate downloaded successfully!', { id: loadingToast });
        } catch (error) {
            logger.error('PDF generation error:', error);
            toast.error('Failed to download certificate. Please try again.', { id: loadingToast });
        } finally {
            setDownloading(false);
        }
    };

    if (result) {
        // Eğer quiz zaten tamamlanmışsa
        if (result.alreadyCompleted) {
            return (
                <div className="max-w-2xl mx-auto container px-6 py-8">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        className="card p-8 text-center"
                    >
                        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-5 rounded-2xl inline-block mb-6">
                            <TrophyIcon className="h-16 w-16 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold mb-4">
                            Quiz Already Completed
                        </h1>
                        <p className="text-lg opacity-70 mb-6">
                            You have already completed this quiz and earned your certificate. You cannot retake the quiz.
                        </p>
                        
                        {result.certificateId && (
                            <div className="bg-light-bg dark:bg-dark-bg p-4 rounded-lg mb-6">
                                <p className="text-sm opacity-70 mb-1">Your Certificate ID</p>
                                <p className="font-mono font-semibold text-lg">{result.certificateId}</p>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            {result.certificateId && (
                                <Link 
                                    to={`/verify/${result.certificateId}`}
                                    className="btn-primary flex items-center justify-center gap-2"
                                >
                                    <ShieldCheckIcon className="h-5 w-5" />
                                    View Certificate
                                </Link>
                            )}
                            <Link 
                                to="/profile"
                                className="btn-secondary flex items-center justify-center gap-2"
                            >
                                <AcademicCapIcon className="h-5 w-5" />
                                Go to Profile
                            </Link>
                        </div>
                    </motion.div>
                </div>
            );
        }

        return (
            <div className="max-w-4xl mx-auto container px-6 py-8">
                {result.passed ? (
                    // ==================== SUCCESS SCREEN ====================
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        className="space-y-6"
                    >
                        {/* Main Success Card */}
                        <div className="card p-8 md:p-10">
                            <div className="flex items-start gap-6 mb-8">
                                <div className="flex-shrink-0">
                                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-2xl">
                                        <CheckCircleIcon className="h-12 w-12 text-white" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <h1 className="text-3xl md:text-4xl font-bold mb-2">
                                        Quiz Completed Successfully
                                    </h1>
                                    <p className="text-lg opacity-70">
                                        Certificate Earned
                                    </p>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                    <div className="text-5xl font-bold text-green-600 dark:text-green-400">
                                        {result.score.toFixed(0)}%
                                    </div>
                                    <p className="text-sm opacity-70 mt-1">Your Score</p>
                                </div>
                            </div>

                            <div className="border-t border-light-border dark:border-dark-border pt-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <p className="text-sm opacity-70 mb-2">Course</p>
                                        <p className="font-semibold">{quiz.title}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm opacity-70 mb-2">Certificate ID</p>
                                        <p className="font-mono text-sm font-semibold">{result.certificateId}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-200 dark:border-green-800">
                                <div className="flex items-start gap-3">
                                    <ShieldCheckIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm">
                                        Your certificate has been issued and added to your profile. You can download it as PDF or share it on social media platforms.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons - Redesigned with Logos */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Download Certificate Card */}
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <button
                                    onClick={handleDownloadCertificate}
                                    disabled={downloading || !certificateData}
                                    className="w-full h-full border-2 border-green-200 dark:border-green-800 rounded-2xl p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 hover:border-green-400 dark:hover:border-green-600 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex flex-col items-center text-center h-full">
                                        <div className="bg-green-500 p-4 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                                            <ArrowDownTrayIcon className="h-8 w-8 text-white" />
                                        </div>
                                        <h3 className="font-bold text-lg text-green-700 dark:text-green-300 mb-2">
                                            {downloading ? 'Generating...' : 'Download Certificate'}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                                            {downloading ? 'Preparing PDF...' : 'Download your certificate as PDF'}
                                        </p>
                                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold text-sm">
                                            <span>Download Now</span>
                                            <svg className="w-4 h-4 group-hover:translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                            </svg>
                                        </div>
                                    </div>
                                </button>
                            </motion.div>

                            {/* LinkedIn Share Card */}
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <button
                                    onClick={handleShareLinkedIn}
                                    className="w-full h-full border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 hover:border-blue-400 dark:hover:border-blue-600 transition-all group"
                                >
                                    <div className="flex flex-col items-center text-center h-full">
                                        <div className="bg-[#0A66C2] p-4 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                                            <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                            </svg>
                                        </div>
                                        <h3 className="font-bold text-lg text-blue-700 dark:text-blue-300 mb-2">
                                            Share on LinkedIn
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                                            Share your achievement with professionals
                                        </p>
                                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                            <span>Post Now</span>
                                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </button>
                            </motion.div>

                            {/* Twitter Share Card */}
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <button
                                    onClick={handleShareTwitter}
                                    className="w-full h-full border-2 border-sky-200 dark:border-sky-800 rounded-2xl p-6 bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 hover:border-sky-400 dark:hover:border-sky-600 transition-all group"
                                >
                                    <div className="flex flex-col items-center text-center h-full">
                                        <div className="bg-black dark:bg-white p-4 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                                            <svg className="h-8 w-8 text-white dark:text-black" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                            </svg>
                                        </div>
                                        <h3 className="font-bold text-lg text-sky-700 dark:text-sky-300 mb-2">
                                            Share on X (Twitter)
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                                            Tweet your achievement to followers
                                        </p>
                                        <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400 font-semibold text-sm">
                                            <span>Tweet Now</span>
                                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </button>
                            </motion.div>
                        </div>

                        {/* Navigation */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/profile" className="btn-primary">
                                View My Profile
                            </Link>
                            <Link to="/courses" className="btn-secondary">
                                Browse Courses
                            </Link>
                        </div>
                        
                        {/* Hidden PDF Certificate (Only for Download) */}
                        {certificateData && (
                            <div className="hidden" id="certificate-pdf-container">
                                <div 
                                    ref={certificateRef}
                                    className="relative bg-white overflow-hidden"
                                    style={{ 
                                        width: '1122px',
                                        height: '794px',
                                        fontFamily: 'system-ui, -apple-system, sans-serif'
                                    }}
                                >
                                    {/* Border Frame */}
                                    <div className="absolute inset-0 border-8 border-gray-800 m-8"></div>
                                    <div className="absolute inset-0 border-2 border-gray-300 m-12"></div>

                                    {/* Content Container */}
                                    <div className="relative h-full flex flex-col" style={{ padding: '60px 80px' }}>
                                        {/* Header with Logo */}
                                        <div className="flex items-center justify-between mb-8">
                                            <div className="flex items-center gap-3">
                                                <img 
                                                    src="/logo-dark.png" 
                                                    alt="AircoreDynamics Logo" 
                                                    style={{ height: '50px', width: 'auto' }}
                                                />
                                                <div>
                                                    <h2 className="text-xl font-bold text-gray-800">AircoreDynamics</h2>
                                                    <p className="text-xs text-gray-600">Aviation Education Platform</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="flex items-center justify-end gap-2 text-green-600 mb-1">
                                                    <ShieldCheckIcon className="h-4 w-4" />
                                                    <span className="text-xs font-bold">VERIFIED</span>
                                                </div>
                                                <p className="text-xs text-gray-500">ID: {certificateData.certificateId}</p>
                                            </div>
                                        </div>

                                        {/* Certificate Title */}
                                        <div className="text-center mb-10">
                                            <h1 className="text-5xl font-bold text-gray-800 mb-2 tracking-wider" style={{ fontFamily: 'serif' }}>
                                                CERTIFICATE
                                            </h1>
                                            <p className="text-xl text-gray-600 tracking-widest">OF COMPLETION</p>
                                            <div className="w-32 h-1 bg-gray-800 mx-auto mt-3"></div>
                                        </div>

                                        {/* Certificate Body */}
                                        <div className="text-center space-y-5 flex-1 flex flex-col justify-center pb-8">
                                            <p className="text-lg text-gray-700">This is to certify that</p>
                                            
                                            <div className="my-2">
                                                <p className="text-5xl font-bold text-gray-800 mb-3" style={{ fontFamily: 'serif' }}>
                                                    {certificateData.user?.name || certificateData.userName}
                                                </p>
                                                <div className="w-64 h-0.5 bg-gray-400 mx-auto"></div>
                                            </div>

                                            <p className="text-lg text-gray-700">has successfully completed</p>

                                            <div className="my-3">
                                                <p className="text-3xl font-semibold text-gray-800 px-12 leading-tight">
                                                    {certificateData.course?.title || certificateData.quizTitle || quiz.title}
                                                </p>
                                            </div>

                                            <p className="text-sm text-gray-600 max-w-xl mx-auto leading-relaxed pt-2">
                                                and has demonstrated comprehensive understanding and proficiency in all 
                                                course requirements and assessments.
                                            </p>
                                            
                                            <p className="text-xs text-gray-500 mt-4">
                                                Course material indexed in MERLOT II (ID: 773476432)
                                            </p>
                                        </div>

                                        {/* Footer */}
                                        <div className="border-t-2 border-gray-300 pt-5">
                                            <div className="flex justify-between items-end">
                                                <div className="text-left">
                                                    <p className="text-xs text-gray-500 mb-2">Date of Completion</p>
                                                    <div className="w-44 border-b-2 border-gray-800 pb-1 mb-1">
                                                        <p className="font-semibold text-gray-800 text-sm">
                                                            {new Date(certificateData.issuedAt).toLocaleDateString('en-US', { 
                                                                month: 'long', 
                                                                day: 'numeric', 
                                                                year: 'numeric' 
                                                            })}
                                                        </p>
                                                    </div>
                                                    <p className="text-xs text-gray-500">Issue Date</p>
                                                </div>

                                                <div className="text-right">
                                                    <p className="text-xs text-gray-500 mb-2">Authorized Signature</p>
                                                    <div className="w-44 border-b-2 border-gray-800 pb-1 mb-1">
                                                        <p className="font-bold text-gray-800 text-lg" style={{ fontFamily: 'cursive' }}>
                                                            AircoreDynamics
                                                        </p>
                                                    </div>
                                                    <p className="text-xs text-gray-500">Platform Administrator</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </motion.div>
                ) : (
                    // ==================== FAILURE SCREEN ====================
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        className="space-y-6"
                    >
                        {/* Main Failure Card */}
                        <div className="card p-8 md:p-10">
                            <div className="flex items-start gap-6 mb-8">
                                <div className="flex-shrink-0">
                                    <div className="bg-gradient-to-br from-orange-500 to-red-600 p-4 rounded-2xl">
                                        <XCircleIcon className="h-12 w-12 text-white" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <h1 className="text-3xl md:text-4xl font-bold mb-2">
                                        Quiz Not Passed
                                    </h1>
                                    <p className="text-lg opacity-70">
                                        Review and try again
                                    </p>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                    <div className="text-5xl font-bold text-orange-600 dark:text-orange-400">
                                        {result.score.toFixed(0)}%
                                    </div>
                                    <p className="text-sm opacity-70 mt-1">Your Score</p>
                                </div>
                            </div>

                            <div className="border-t border-light-border dark:border-dark-border pt-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div>
                                        <p className="text-sm opacity-70 mb-1">Your Score</p>
                                        <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                            {result.score.toFixed(1)}%
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm opacity-70 mb-1">Required</p>
                                        <p className="text-2xl font-bold">
                                            {quiz.passingScore}%
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm opacity-70 mb-1">Gap</p>
                                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                            {Math.max(0, quiz.passingScore - result.score).toFixed(1)}%
                                        </p>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="mt-6">
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="opacity-70">Progress to Passing</span>
                                        <span className="font-semibold">{Math.min(100, (result.score / quiz.passingScore * 100)).toFixed(0)}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${Math.min(100, (result.score / quiz.passingScore * 100))}%` }}
                                            transition={{ duration: 1, delay: 0.3 }}
                                            className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/10 rounded-xl border border-orange-200 dark:border-orange-800">
                                <p className="text-sm">
                                    You can retake this quiz after <span className="font-semibold">24 hours</span>. Use this time to review the course materials and strengthen your understanding.
                                </p>
                            </div>
                        </div>

                        {/* Study Recommendations */}
                        <div className="card p-8">
                            <h2 className="text-xl font-bold mb-6">Recommended Next Steps</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex gap-4 p-4 rounded-lg bg-light-bg dark:bg-dark-bg">
                                    <div className="flex-shrink-0">
                                        <div className="bg-blue-500 p-2.5 rounded-lg">
                                            <BookOpenIcon className="h-5 w-5 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Review Course Materials</h3>
                                        <p className="text-sm opacity-70">Go through the lessons again carefully</p>
                                    </div>
                                </div>

                                <div className="flex gap-4 p-4 rounded-lg bg-light-bg dark:bg-dark-bg">
                                    <div className="flex-shrink-0">
                                        <div className="bg-purple-500 p-2.5 rounded-lg">
                                            <ClockIcon className="h-5 w-5 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Take Your Time</h3>
                                        <p className="text-sm opacity-70">Ensure you understand each concept</p>
                                    </div>
                                </div>

                                <div className="flex gap-4 p-4 rounded-lg bg-light-bg dark:bg-dark-bg">
                                    <div className="flex-shrink-0">
                                        <div className="bg-green-500 p-2.5 rounded-lg">
                                            <LightBulbIcon className="h-5 w-5 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Focus on Key Topics</h3>
                                        <p className="text-sm opacity-70">Identify and study challenging areas</p>
                                    </div>
                                </div>

                                <div className="flex gap-4 p-4 rounded-lg bg-light-bg dark:bg-dark-bg">
                                    <div className="flex-shrink-0">
                                        <div className="bg-orange-500 p-2.5 rounded-lg">
                                            <ArrowPathIcon className="h-5 w-5 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">Retry After 24 Hours</h3>
                                        <p className="text-sm opacity-70">Come back prepared and confident</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button 
                                onClick={() => navigate(-1)} 
                                className="btn-primary"
                            >
                                Return to Course
                            </button>
                            <Link to="/courses" className="btn-secondary">
                                Browse Other Courses
                            </Link>
                        </div>
                    </motion.div>
                )}
            </div>
        );
    }
    
    const currentQ = quiz.questions[currentQuestion];

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            {/* Header - Timer ve Progress */}
            <motion.div 
                initial={{ opacity: 0, y: -20 }} 
                animate={{ opacity: 1, y: 0 }}
                className="card p-4 md:p-6 sticky top-4 z-10 shadow-xl"
            >
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold">{quiz.title}</h1>
                        <p className="text-sm opacity-70 mt-1">
                            Question {currentQuestion + 1} / {quiz.questions.length}
                        </p>
                    </div>
                    
                    {timeLeft !== null && (
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-lg ${timeLeft < 300 ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400' : 'bg-light-bg dark:bg-dark-bg'}`}>
                            <ClockIcon className="h-6 w-6" />
                            {formatTime(timeLeft)}
                        </div>
                    )}
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                    <div className="flex justify-between text-sm mb-2">
                        <span className="opacity-70">Progress</span>
                        <span className="font-semibold">{Object.keys(answers).length} / {quiz.questions.length} answered</span>
                    </div>
                    <div className="w-full bg-light-bg dark:bg-dark-bg rounded-full h-3 overflow-hidden">
                        <motion.div 
                            className="bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 h-3 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>
                </div>
            </motion.div>

            {/* Question Navigator */}
            <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="card p-4"
            >
                <p className="text-sm opacity-70 mb-3">Quick Access:</p>
                <div className="flex flex-wrap gap-2">
                    {quiz.questions.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentQuestion(idx)}
                            className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                                currentQuestion === idx 
                                    ? 'bg-light-primary dark:bg-dark-primary text-white scale-110' 
                                    : answers[idx] !== undefined
                                        ? 'bg-green-500 text-white'
                                        : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                            }`}
                        >
                            {idx + 1}
                        </button>
                    ))}
                </div>
            </motion.div>

            {/* Question Card */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentQuestion}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                    className="card p-6 md:p-8"
                >
                    <div className="mb-6">
                        <div className="flex items-start gap-3 mb-6">
                            <span className="flex-shrink-0 w-10 h-10 bg-light-primary dark:bg-dark-primary text-white rounded-full flex items-center justify-center font-bold">
                                {currentQuestion + 1}
                            </span>
                            <p className="text-xl md:text-2xl font-semibold flex-1">{currentQ.questionText}</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {currentQ.options.map((option, oIndex) => (
                            <motion.label
                                key={oIndex}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`flex items-center p-4 rounded-xl cursor-pointer transition-all border-2 ${
                                    answers[currentQuestion] === option
                                        ? 'border-light-primary dark:border-dark-primary bg-light-primary/10 dark:bg-dark-primary/10'
                                        : 'border-light-border dark:border-dark-border hover:border-light-primary/50 dark:hover:border-dark-primary/50'
                                }`}
                            >
                                <input
                                    type="radio"
                                    name={`question-${currentQuestion}`}
                                    value={option}
                                    checked={answers[currentQuestion] === option}
                                    onChange={() => handleAnswerChange(currentQuestion, option)}
                                    className="mr-4 h-5 w-5 accent-light-primary dark:accent-dark-primary"
                                />
                                <span className="text-lg flex-1">{option}</span>
                                {answers[currentQuestion] === option && (
                                    <CheckCircleIcon className="h-6 w-6 text-light-primary dark:text-dark-primary" />
                                )}
                            </motion.label>
                        ))}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-8 gap-4">
                        <button
                            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                            disabled={currentQuestion === 0}
                            className="btn-secondary"
                        >
                            ← Previous
                        </button>
                        
                        {currentQuestion < quiz.questions.length - 1 ? (
                            <button
                                onClick={() => setCurrentQuestion(prev => Math.min(quiz.questions.length - 1, prev + 1))}
                                className="btn-primary"
                            >
                                Next →
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                className="btn-primary !bg-green-600 hover:!bg-green-700"
                            >
                                <CheckCircleIcon className="h-5 w-5 inline mr-2" />
                                Complete Quiz
                            </button>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>

            {error && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="card p-4 bg-red-50 dark:bg-red-900/10 border-red-500"
                >
                    <p className="text-red-500 text-center">{error}</p>
                </motion.div>
            )}
        </div>
    );
};

export default QuizPage;